import React, { useState, useEffect, useContext, useMemo } from 'react'
import styled from 'styled-components'
import { css } from '@styled-system/css'

import { Flex, Fade, Box } from 'ui/acro'
import { P } from 'ui/bend'
import { SeeMoreButton, SeriesBox } from '../MyPracticeStyles'
import SummaryBanner from 'components_v2/MyPractice/SummaryBanner'
import MyPracticeNotData from 'components_v2/MyPractice/MyPracticeNotData'
import AloMovesAnimation from 'components_v2/Search/common/AloMovesAnimation'
import { LoadMore } from './loadMoreItems'
import SortableList from 'components_v1/sortableList/SortableList'
import {
  ClassCard,
  SeriesCard
} from 'components_v1/SchemaSectionRenderer/common/components'
import MyPracticeLeftFilter from '../../MyPracticeLeftFilter'
import MyPracticeFilteredList from '../../MyPracticeFilteredList'
import { connect } from 'react-redux'
import { getPlanEntryGridState } from '../../../../../javascript/redux/selectors/planEntries'
import usePlaylistDrawer from 'hooks/usePlaylistDrawer'
import { useUserContext } from 'context'
import { axios } from 'api'
import { camelToSnake } from 'utils'
import { MyPracticeDetailsContext } from 'components_v2/page_context'

const Container = styled('div')`
  padding: 0 40px;
  .SelectedFiltersDisplay {
    margin: 0;
  }
  .MyPracticeLeftSection {
    margin-top: 34px;
  }
  .ContentWrapper {
    @media (max-width: 1024px) {
      flex-direction: column;
    }
  }
  @media (max-width: 1024px) {
    padding: 0;
  }
`
const RightContainer = styled('div')`
  width: 80%;
  @media (max-width: 1024px) {
    width: 100%;
    .CompletedClassCardContainer {
      justify-content: space-between;
    }

    .CompletedCardContainer-Grid {
      a {
        min-width: 150px !important;
      }
    }
  }
`

const FilterWrapper = styled(Flex)(
  css({
    paddingTop: '[0, 0]',
    // pl: "['6%', '0']",
    alignItems: 'end',
    justifyContent: 'space-between',
    '@media (max-width: 1024px)': {
      // flexDirection: 'column-reverse',
      gap: '1rem',
      alignItems: 'start',
      pr: '5%',
      pl: '5%'
    }
  }),
  {
    '& .SortableList': {
      '@media (max-width: 1024px)': {
        width: 'fit-content'
      }
    },
    '.SortableList--left': {
      '@media (max-width: 1024px)': {
        display: 'none'
      }
    }
  }
)

const sortOptions = [
  'date',
  'oldest',
  'az',
  'durationShortToLong',
  'durationLongToShort'
]

const CompletedClassesComponent = ({
  userCompletedClasses,
  summary,
  fetchDataCompletedClasses,
  loading,
  filterOption,
  perPage,
  setPerPage,
  setPage,
  page,
  favoriteCallback,
  setUserCompletedClasses,
  removeFromCompletedCallback,
  showDurationSort = true,
  ...props
}) => {
  const { completedContext, daysContext } = useContext(MyPracticeDetailsContext)
  const [selectedOptions, setSelectedOptions] = completedContext
  const [days, setDays] = daysContext

  const [cards, setCards] = useState([])
  const { user } = useUserContext() || {}

  useEffect(() => {
    setCards(userCompletedClasses)
  }, [userCompletedClasses])

  const updatePlaylistList = (updatedPlaylistIds) => {
    const updatedCards = cards.map((card) =>
      card.id === selectedCardData?.id
        ? { ...card, playlistIds: updatedPlaylistIds }
        : card
    )
    setUserCompletedClasses(updatedCards)
  }

  const { handlePlaylistIconClick, AddToPlaylistDrawer, selectedCardData } =
    usePlaylistDrawer(updatePlaylistList)

  const filter_applied = (option) => {
    if ((option?.value ?? option) !== days) {
      setDays(option)
      filterOption(option)
    }
  }

  const loadMore = () => {
    LoadMore(
      setPage,
      setPerPage,
      fetchDataCompletedClasses,
      'completed',
      page,
      perPage,
      days
    )
  }

  useEffect(() => {
    if (props.currentTab === 'completed' && sortOptions.includes(props.order)) {
      onSortDropDownChange(props.order)
    }
  }, [props.order])

  useEffect(() => {
    const { selectedColumn } = selectedOptions
    const isOnlyClassesSelected =
      props.filteredAttributes?.type?.length === 1 &&
      props.filteredAttributes?.type[0]?.type === 'classes'

    if (
      props.currentTab === 'completed' &&
      !isOnlyClassesSelected &&
      selectedColumn === 'duration'
    ) {
      onSortDropDownChange('date')
    }
  }, [props?.filteredAttributes?.type])

  const onSortDropDownChange = (sortOption) => {
    const { selectedColumn, sorting } = selectedOptions
    let options = {}

    if (sortOption === 'oldest') {
      options = {
        sorting: 'asc',
        selectedColumn: 'date'
      }
    } else if (sortOption === 'date') {
      options = {
        sorting: 'desc',
        selectedColumn: sortOption
      }
    } else if (sortOption === 'durationShortToLong') {
      options = {
        sorting: 'asc',
        selectedColumn: 'duration'
      }
    } else if (sortOption === 'durationLongToShort') {
      options = {
        sorting: 'desc',
        selectedColumn: 'duration'
      }
    } else {
      options = {
        sorting: 'asc',
        selectedColumn: sortOption
      }
    }
    if (
      selectedColumn !== options.selectedColumn ||
      sorting !== options.sorting
    ) {
      setSelectedOptions(options)
    }
  }

  const isCompleted = useMemo(
    () =>
      !Array.isArray(cards) ||
      cards?.length === 0 ||
      (Array.isArray(cards) && cards?.length >= summary?.totalCount),
    [cards, summary]
  )

  const removeFromCompleted = async (type, entryId) => {
    try {
      const res = await axios.delete(`my_practice/completed/${type}/${entryId}`)

      if (res.status === 200) {
        if (res?.data?.ga4Event) {
          window.dataLayer ||= []
          window.dataLayer.push(camelToSnake(res.data?.ga4Event))
        }
        removeFromCompletedCallback(entryId)
      }
    } catch (e) {
      console.error(e)
    } finally {
    }
  }

  return (
    <Fade appear={true} mt={['18px', '66px']}>
      {loading && <AloMovesAnimation />}
      {!loading && summary && (
        <Box width={'100%'} mb={['18px', '32px']}>
          <SummaryBanner summary={summary} />
        </Box>
      )}
      <Container>
        {window.innerWidth > 1024 ? (
          <MyPracticeFilteredList
            includeFilters={props.includeFilters}
            dropdownOptions={props.dropdownOptions}
            preloadedFilters={props.preloadedFilters}
            baseUrl={props.baseUrl}
            withSubcategories={props.withSubcategories}
          />
        ) : null}
        <Flex gap='2%' className='ContentWrapper'>
          <MyPracticeLeftFilter
            withSubcategories={props.withSubcategories}
            // autopaginate={props.autopaginate}
            includeFilters={props.includeFilters}
            // planEntries={props.planEntries}
            preloadedFilters={props.preloadedFilters}
            baseUrl={props.baseUrl}
            dropdownOptions={props.dropdownOptions}
            parent_tab={'completed'}
            currentTab={props.currentTab}
            fetchMyPracticeInfo={fetchDataCompletedClasses}
            currentUser={user}
            totalCount={summary?.totalCount}
          ></MyPracticeLeftFilter>
          <RightContainer>
            <FilterWrapper
              paddingTop={[0, 0]}
              pl={['5%', '5px']}
              justifyContent='space-between'
            >
              <P id='my-practice-items-count'>
                {summary?.totalCount} Classes & Programs
              </P>
              <Flex alignItems='center' gap='16px'>
                <SortableList
                  sortableFields={['7', '30', '90', 'all']}
                  order={days}
                  onSort={filter_applied}
                  alignLeftXs={false}
                  sortLocation={'Mypratice-completed'.toLowerCase()}
                />
                <SortableList
                  sortableFields={
                    showDurationSort ? sortOptions : sortOptions?.slice(0, -2)
                  }
                  order={
                    selectedOptions.selectedColumn === 'duration'
                      ? selectedOptions.sorting === 'asc'
                        ? 'durationShortToLong'
                        : 'durationLongToShort'
                      : selectedOptions.selectedColumn === 'date'
                      ? selectedOptions.sorting === 'asc'
                        ? 'oldest'
                        : 'date'
                      : selectedOptions.selectedColumn
                  }
                  onSort={onSortDropDownChange}
                  alignLeftXs={true}
                  sortLocation={'Mypratice-completed'.toLowerCase()}
                />
              </Flex>
            </FilterWrapper>

            {!loading && userCompletedClasses?.length <= 0 && (
              <MyPracticeNotData
                option={'completed'}
                text={'NO VIDEOS HERE YET...'}
              />
            )}

            {!loading && userCompletedClasses?.length > 0 && (
              <Flex
                style={{
                  // margin: 'auto',
                  justifyContent: 'center',
                  flexDirection: 'column',
                  alignItems: 'center'
                }}
              >
                <SeriesBox
                  id='Users-Series'
                  inTrigger={cards && true}
                  appear={true}
                  className='CompletedClassCardContainer'
                >
                  {cards?.map((classItem) => {
                    return (
                      <Box
                        key={classItem?.entryId || classItem.id}
                        width={
                          cards?.length === 1
                            ? ['100%', '100%', 'auto', 'auto']
                            : props.layout === 'list'
                            ? ['100%', '33.33%', '33.33%', '33.33%']
                            : ['45%', '33.33%', '33.33%', '33.33%']
                        }
                        p={['1rem 0', '1rem', '2rem', '2rem']}
                        borderBottom={['1px solid #ECECEC', '0']}
                        style={{
                          position: 'relative'
                        }}
                        className={
                          props.layout === 'list'
                            ? 'CompletedCardContainer-List'
                            : 'CompletedCardContainer-Grid'
                        }
                      >
                        {classItem?.itemType === 'program' ? (
                          <SeriesCard
                            {...classItem}
                            isFromMyPracticeCompleted={true}
                            membershipTier={user?.membershipTier}
                            onRemove={() =>
                              removeFromCompleted('program', classItem?.entryId)
                            }
                          />
                        ) : (
                          <ClassCard
                            {...classItem}
                            humanDuration={classItem.duration}
                            videoThumbnail={classItem.image}
                            plan={classItem.plan}
                            alignBox={'left'}
                            variantTitle='secondary'
                            variantSubtitle='secondary'
                            planEntryUrl={classItem.classUrl}
                            favoriteCallback={favoriteCallback}
                            isGridView={props.layout === 'tile'}
                            membershipTier={user?.membershipTier}
                            onPlaylistIconClick={() =>
                              handlePlaylistIconClick(classItem)
                            }
                            isFromMyPracticeCompleted={true}
                            onRemove={() =>
                              removeFromCompleted('class', classItem?.entryId)
                            }
                            pb='20px'
                          />
                        )}
                      </Box>
                    )
                  })}
                </SeriesBox>

                {!loading && !isCompleted && (
                  <SeeMoreButton onClick={loadMore}>See More</SeeMoreButton>
                )}
                {AddToPlaylistDrawer}
              </Flex>
            )}
          </RightContainer>
        </Flex>
      </Container>
    </Fade>
  )
}

const mapStateToProps = (state) => {
  const planEntryGridState = getPlanEntryGridState(state)
  return {
    order: state.ui.planEntries.order,
    filteredAttributes: state.ui.planEntries.filtered,
    layout: planEntryGridState.layout
  }
}

const mapDispatchToProps = (dispatch) => {
  return {}
}
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(CompletedClassesComponent)
